// src/oura/components/newscan/DecisionCard.tsx
//
// One thing staff settle before the scan: what is wrong, the evidence, and
// the fix. A white block with a tinted icon square (rose for a blocker, amber
// for a decision, blue for a Search Console suggestion, grey for a note), a
// 14px semibold title, a 13px line on why, the question wordings it is about
// in grey inner rows, and the buttons: the recommended fix in blue (verb
// first, names the result), the alternatives as white pills, and the manual
// routes ("Edit 44 myself", "Leave it out") as text buttons. An option that
// puts in new wording ("Use this wording", "Use the other") shows it in a grey
// row ("New: ...") directly above its own pill, so the pill stays short and
// two wordings are never confused.
//
// Two ways in:
// - `DecisionCard`: any decision, with its actions spelled out. The Website
//   and Business steps use it for the client-side cards ("This site is
//   already a client", "<Area> is not on the website").
// - `DraftDecisionCard`: a decision from the draft (ScanDraftDecision). It
//   builds the buttons from `recommended` and `alternatives` and hands back
//   the DecisionChoice to send, plus the option, so the caller can run a
//   client-side action (open_editor, open_word, connect_gsc, choose_each)
//   instead of a write.

import { CircleAlertIcon, CircleHelpIcon, InfoIcon, MessageSquareIcon, SearchIcon, type LucideIcon } from "lucide-react";
import { useId, type ReactNode } from "react";
import {
    CLIENT_SIDE_ACTIONS,
    type DecisionAction,
    type DecisionChoice,
    type DecisionOption,
    type DecisionSeverity,
    type ScanDraftDecision,
    type ScanDraftWord,
} from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { IconSquare, PillButton, SURFACE, TAP_TARGET, TYPE, type Tone } from "@oura/ui";
import { DECISION } from "@oura/newscan/copy";
import { Muted } from "./Muted";
import { SlotText } from "./SlotText";
import { isFirstCheckCard } from "./firstCheckFacts";

export interface DecisionCardAction {
    /** Stable key; `DraftDecisionCard` uses "recommended" and "alt:<i>". */
    key: string;
    label: string;
    /** primary = the recommended fix (one per card); secondary = an alternative; text = a manual route. */
    kind: "primary" | "secondary" | "text";
    onClick(): void;
    busy?: boolean;
    disabled?: boolean;
    /**
     * Only moves the screen (Type one, Edit 12 myself): stays pressable while a
     * write runs. Disabling the pressed button under focus sent the next keys
     * back into the word field that was just left.
     */
    movesOnly?: boolean;
    icon?: LucideIcon;
    /** The wording this action puts in, shown above its pill as "New: ...". */
    preview?: ReactNode;
    /**
     * The name a screen reader hears, when the label alone repeats across cards
     * ("Use this wording, question 12"). Starts with the label.
     */
    ariaLabel?: string;
}

export interface DecisionCardProps {
    severity: DecisionSeverity;
    title: ReactNode;
    /** Why it matters, one or two short sentences. */
    detail?: ReactNode;
    /** Question wordings shown as evidence, in order. */
    quotes?: string[];
    /** A fact under the quotes ("“physio bangsar”: 2,400 searches a year"). */
    evidence?: ReactNode;
    /** Where the offered values come from, under the buttons ("Seen on 3 nearby physio sites: RM100 to RM150"). */
    source?: ReactNode;
    /** A First AI check's form card: "From Sarah's form, 14 Oct", under the buttons with a form icon. */
    origin?: ReactNode;
    /** The draft's words, so a blank in a quote reads "<label> needed". */
    words?: Record<string, ScanDraftWord>;
    /** Overrides the severity's icon. */
    icon?: LucideIcon;
    /** Overrides the severity's tone, e.g. green once a card is answered and stays on screen. */
    tone?: Tone;
    actions?: DecisionCardAction[];
    /** Every button off, e.g. while another write runs. */
    disabled?: boolean;
    /** Extra body under the detail (a choose-each list, an inline field). */
    children?: ReactNode;
    /** The title's heading level. Default 2 (the step title is the h1). */
    level?: 2 | 3;
    id?: string;
    className?: string;
}

const SEVERITY: Record<DecisionSeverity, { tone: Tone; icon: LucideIcon }> = {
    blocker: { tone: "rose", icon: CircleAlertIcon },
    decide: { tone: "amber", icon: CircleHelpIcon },
    suggestion: { tone: "blue", icon: SearchIcon },
    info: { tone: "gray", icon: InfoIcon },
};

const KIND_ORDER: Record<DecisionCardAction["kind"], number> = { primary: 0, secondary: 1, text: 2 };

export function DecisionCard({
    severity,
    title,
    detail,
    quotes,
    evidence,
    source,
    origin,
    words,
    icon,
    tone: toneOverride,
    actions = [],
    disabled,
    children,
    level = 2,
    id,
    className,
}: DecisionCardProps) {
    const titleId = useId();
    const { tone: severityTone, icon: severityIcon } = SEVERITY[severity];
    const tone = toneOverride ?? severityTone;
    const Heading = level === 3 ? "h3" : "h2";
    const sorted = [...actions].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
    const anyBusy = actions.some((a) => a.busy);
    return (
        <section
            id={id}
            aria-labelledby={titleId}
            className={cn(
                severity === "suggestion" ? "rounded-2xl bg-blue-50/50 p-4 ring-1 ring-inset ring-blue-100" : SURFACE.block,
                "flex items-start gap-3",
                className,
            )}
        >
            <IconSquare tone={tone} icon={icon ?? severityIcon} />
            <div className="min-w-0 flex-1">
                <Heading id={titleId} className={cn(TYPE.tb, "m-0 break-words pt-1.5 font-semibold text-gray-900")}>
                    <span className="sr-only">{DECISION.severity[severity]}: </span>
                    {title}
                </Heading>
                {detail && <div className={cn(TYPE.tc, "mt-1 whitespace-pre-line break-words text-gray-600")}>{detail}</div>}
                {quotes && quotes.length > 0 && (
                    <ul role="list" className="list-plain m-0 mt-2.5 space-y-1.5 p-0">
                        {quotes.map((q, i) => (
                            <li key={`${i}-${q}`} className={cn(SURFACE.inner, TYPE.tc, "break-words text-gray-700")}>
                                <SlotText text={q} words={words} />
                            </li>
                        ))}
                    </ul>
                )}
                {evidence && <p className={cn(TYPE.tc, "m-0 mt-2 break-words text-gray-500")}>{evidence}</p>}
                {children && <div className="mt-3">{children}</div>}
                {sorted.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2.5">
                        {sorted.map((a) => {
                            const pill = (
                                <PillButton
                                    key={a.key}
                                    size="sm"
                                    variant={a.kind}
                                    icon={a.icon}
                                    busy={a.busy}
                                    disabled={a.movesOnly ? a.disabled : disabled || a.disabled || (anyBusy && !a.busy)}
                                    onClick={a.onClick}
                                    aria-label={a.ariaLabel}
                                    aria-describedby={a.preview ? `${titleId}-${a.key}` : undefined}
                                    // 28px pill, 44px target (kit rule): never a bigger visible pill on phone.
                                    className={a.kind === "text" ? undefined : TAP_TARGET}
                                >
                                    {a.label}
                                </PillButton>
                            );
                            if (!a.preview) return pill;
                            return (
                                <div key={a.key} className="flex basis-full flex-col items-start gap-1.5">
                                    <p id={`${titleId}-${a.key}`} className={cn(SURFACE.inner, TYPE.tc, "m-0 w-full break-words text-gray-700")}>
                                        <span className="font-medium text-gray-900">{DECISION.previewLead}</span> {a.preview}
                                    </p>
                                    {pill}
                                </div>
                            );
                        })}
                    </div>
                )}
                {source && (
                    <Muted as="p" className="mt-2 break-words">
                        {source}
                    </Muted>
                )}
                {origin && (
                    <Muted as="p" tone="ink4" className="mt-2 flex items-start gap-1.5 break-words">
                        <MessageSquareIcon aria-hidden size={12} className="mt-[3px] shrink-0" />
                        <span className="min-w-0">{origin}</span>
                    </Muted>
                )}
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * A decision from the draft
 * ------------------------------------------------------------------------- */

/** Manual routes read as text buttons: the staffer does it, Oura does not. */
const TEXT_ACTIONS: ReadonlyArray<DecisionAction["kind"]> = ["open_editor", "open_word", "dismiss"];

export interface DraftDecisionCardProps {
    decision: ScanDraftDecision;
    /**
     * Called with the choice to send (`answer(decision.id, choice)`) and the
     * option picked. For a client-side action (`CLIENT_SIDE_ACTIONS`), move
     * the screen instead of writing.
     */
    onPick(choice: DecisionChoice, option: DecisionOption): void;
    /** The key of the option whose write is running ("recommended", "alt:1"): it spins. */
    pendingKey?: string | null;
    /** Every button off (another write runs). */
    disabled?: boolean;
    /** Extra body (RecipeChangeCard's choose-each list). */
    children?: ReactNode;
    level?: 2 | 3;
    /** The draft's words, for the blanks in the quotes. */
    words?: Record<string, ScanDraftWord>;
    className?: string;
}

/**
 * An option may carry the wording it puts in (`preview`), so its label can
 * stay short. Read through this widening until the contract's DecisionOption
 * declares the optional field.
 */
type PreviewedOption = DecisionOption & { preview?: string | null };

const previewOf = (o: DecisionOption): string | undefined => (o as PreviewedOption).preview || undefined;

/** Screen reader names for options whose label repeats across cards. */
const OPTION_NAME = {
    forQuestions: (label: string, ns: number[]) =>
        ns.length === 1 ? `${label}, question ${ns[0]}` : `${label}, questions ${ns.slice(0, -1).join(", ")} and ${ns[ns.length - 1]}`,
    about: (name: string, title: string) => `${name}. ${title}`,
} as const;

/**
 * Three cards can each offer "Use this wording", twelve "Type one": the name a
 * screen reader hears adds the questions it is about and, when that alone does
 * not tell the buttons apart, the card's title. The wording itself is already
 * read as the button's description.
 */
function nameOf(o: DecisionOption, d: ScanDraftDecision): string {
    const ns = "n" in o.action ? [o.action.n] : d.slots;
    const addN = ns.length > 0 && !ns.some((n) => new RegExp(`\\b${n}\\b`).test(o.label));
    const name = addN ? OPTION_NAME.forQuestions(o.label, ns) : o.label;
    return addN && previewOf(o) ? name : OPTION_NAME.about(name, d.title);
}

export function DraftDecisionCard({ decision, onPick, pendingKey, disabled, children, level, words, className }: DraftDecisionCardProps) {
    const suggestion = decision.severity === "suggestion";
    // A First AI check's cards (form, quote, For the plan): one blue choice, the rest text (JOURNEY-SPEC §2.5).
    const allText = isFirstCheckCard(decision);
    const actions: DecisionCardAction[] = [];
    const rec = decision.recommended;
    if (rec) {
        actions.push({
            key: "recommended",
            label: rec.label,
            // A suggestion is optional: no blue button on it.
            kind: suggestion ? "secondary" : "primary",
            preview: previewOf(rec),
            ariaLabel: nameOf(rec, decision),
            busy: pendingKey === "recommended",
            movesOnly: CLIENT_SIDE_ACTIONS.includes(rec.action.kind),
            onClick: () => onPick("recommended", rec),
        });
    }
    decision.alternatives.forEach((alt, i) => {
        actions.push({
            key: `alt:${i}`,
            label: alt.label,
            kind: allText || TEXT_ACTIONS.includes(alt.action.kind) ? "text" : "secondary",
            preview: previewOf(alt),
            ariaLabel: nameOf(alt, decision),
            busy: pendingKey === `alt:${i}`,
            movesOnly: CLIENT_SIDE_ACTIONS.includes(alt.action.kind),
            onClick: () => onPick({ alternative: i }, alt),
        });
    });
    return (
        <DecisionCard
            severity={decision.severity}
            title={decision.title}
            detail={decision.detail}
            quotes={decision.quotes}
            evidence={decision.evidence ? DECISION.evidence(decision.evidence.query, decision.evidence.searches) : undefined}
            source={decision.source || undefined}
            origin={decision.origin || undefined}
            words={words}
            actions={actions}
            disabled={disabled}
            level={level}
            className={className}
        >
            {children}
        </DecisionCard>
    );
}
